import {
  LIMB_IDS,
  LIMB_MAX_HP,
  type ContainerKind,
  type DeathCause,
  type EquipmentRecord,
  type FriendEntry,
  type FriendRef,
  type GridState,
  type Inventory,
  type LimbId,
  type PartyMemberWire,
  type ProfileWire,
  type RaidSummary,
} from "@rietz/shared";
import { create } from "zustand";
import type { BuildPlan } from "./workbench/buildLogic.js";

/**
 * THE game → UI bridge. The HUD is a React island over the canvas; the game is
 * a 60 Hz imperative loop. The contract: plain data only (no three.js objects,
 * sockets or callbacks); the game pushes, React pulls (nothing in `ui/` reaches
 * back — the one exception is the respawn callback handed over at mount); one
 * `set()` per event and only on change (`pushSnapshot` writes the whole health
 * block at once; ammo is separate because it is predicted per frame).
 */

export type HitMarkerKind = "predicted" | "hit" | "kill";

export interface HitMarker {
  /**
   * `performance.now()` of the marker; doubles as the change token so two
   * identical markers still pulse twice.
   */
  at: number;
  kind: HitMarkerKind;
}

/**
 * The world container the player has open. Mirrors `containerState`; `grid` is
 * replaced wholesale on every change.
 */
export interface OpenContainer {
  id: string;
  /**
   * `"stash"` has no world object: the menu publishes the profile's stash
   * through this slot so drag, quickmove, menu and bench work unchanged.
   */
  kind: ContainerKind | "corpse" | "stash";
  name: string;
  grid: GridState;
  /**
   * The worn slots of a body — present exactly for a corpse, and what the
   * screen keys the loot panel on. `grid` is then the pockets, and a rig or
   * backpack here is in wrapped form. See `shared/inventory/body.ts`.
   */
  equipment?: EquipmentRecord;
}

/** One search target's progress — see ui/inventory/search.ts. */
export interface SearchView {
  /** "covered": unsearched overlay up. "searching": reveal sweep running. "done": fully searched. */
  phase: "covered" | "searching" | "done";
  /** iids currently hidden (render as "?" and inert). */
  hiddenIids: readonly string[];
  /** The item the lens hovers over (next to be revealed), during "searching". */
  focusIid: string | null;
}

/**
 * Which kind of server, decided by whichever lands first after `hello`:
 * `profile` (meta) or `welcome` (direct). `null` until then, and forever for
 * `?offline=1`. Sticky across reconnects — a meta server can answer a
 * reconnect with `welcome`.
 */
export type ServerMode = "meta" | "direct";

/**
 * Where the player is: connecting (scrim only), menu (meta only), queue,
 * deploying (`welcome` landed, map downloading), raid, summary, hideout
 * (walking the shelter — pointer locked like a raid; meta only). A direct
 * server only ever uses connecting → raid, so nothing in the raid HUD keys off this.
 */
export type Screen =
  | "connecting"
  | "menu"
  | "queue"
  | "deploying"
  | "raid"
  | "summary"
  | "hideout";

/**
 * One line of the ladder from `GET /leaderboard`. Client-side rather than
 * shared: the only menu data not on the WebSocket, and nothing is decided from
 * it. `name` is a display name — not unique — so the board is a wall, not a directory.
 */
export interface LeaderboardRow {
  name: string;
  level: number;
  xp: number;
  raids: number;
  extracts: number;
}

/** THE friends state, whole — the shape the `friends` message carries. */
export interface FriendsState {
  friends: FriendEntry[];
  /** Requests sent TO us: the ones `friendRespond` answers. */
  incoming: FriendRef[];
  /** Requests we sent and are waiting on. */
  outgoing: FriendRef[];
}

/** THE party, whole. `members` is in join order, the leader flagged. */
export interface PartyState {
  members: PartyMemberWire[];
}

/**
 * The last refusal for a social verb. Kept because the friends tab cannot grey
 * its buttons in advance — only the server knows. `opId` tells a fresh refusal
 * from the one already shown; `reasonLabel` renders the code.
 */
export interface SocialError {
  opId: number;
  reason: string;
}

/**
 * Which kind of floating dialog a `DialogRef` names: a box's contents, or one
 * item inspected. The kind is part of the identity.
 */
export type DialogKind = "box" | "inspect";

/** One floating dialog: what kind of window, and what it is about. */
export interface DialogRef {
  t: DialogKind;
  iid: string;
}

/**
 * Which menu tab is up. Store state rather than `MetaScreen`'s because a
 * right-click on the Gear tab has to open the Market tab. Order is the strip's
 * (`TAB_LABELS` in `screen.tsx` is a table over this union).
 */
export type MenuTab =
  | "gear"
  | "profile"
  | "traders"
  | "market"
  | "hideout"
  | "quests"
  | "friends"
  | "settings";

/**
 * What the market was asked to narrow to: `item` — this def only; `linked` —
 * everything directly associated (`linkedDefIds`, one hop); `build` — the
 * handful of parts a workbench plan still has to be bought. `null` is the
 * ordinary market. `seq` counts requests: arriving resets search/sub-tab/
 * category once per request, and asking twice is two arrivals.
 */
export type MarketFocus =
  | {
      t: "item" | "linked";
      defId: string;
      /** Bumps on every `openMarket`, so the tab can tell a repeat request from
       *  a rerender. */
      seq: number;
    }
  | { t: "build"; defIds: readonly string[]; seq: number };

/**
 * The fullscreen workbench: which gun is on it, and the PLAN — the parts the
 * player has picked but not yet fitted (`workbench/buildLogic.ts`). An iid
 * rather than an instance, like `modWeaponIid`: every op replaces the object,
 * and the screen re-resolves it. `open` false parks the plan so leaving the
 * bench and coming back does not throw the build away.
 */
export interface WorkbenchState {
  weaponIid: string;
  plan: BuildPlan;
  open: boolean;
}

/**
 * The shooting range's readout while at the lane. A HUD slot because the GAME
 * produces the numbers. `drillSecLeft` is `null` with no timed drill;
 * `drillScore`/`best` are the last result and the session's best. Written
 * every frame, so `setHideoutRange` compares field by field.
 */
export interface RangeHud {
  /** What is in the player's hands, for the corner of the readout. */
  weaponName: string;
  shots: number;
  hits: number;
  /** Seconds left in the running drill, or `null` when none is running. */
  drillSecLeft: number | null;
  /** The current (or just-finished) drill's score. */
  drillScore: number;
  /** The best drill score this visit. */
  best: number;
}

/** The death screen's forensics; `null` while alive. */
export interface DeathInfo {
  cause: DeathCause;
  /** Display name of the killer; "" for a self-credited death. */
  killerName: string;
  /** Item def name of the gun or grenade; "" when unknown. */
  weapon: string;
  /** Human label of the killing limb (`LIMB_LABELS`); "" when unknown. */
  limb: string;
  /** Metres to the killer, or `null`. */
  distM: number | null;
  /** `performance.now()` when the frozen camera ends; `null` when there is none. */
  killcamUntilMs: number | null;
}

export interface HudState {
  // --- Meta / lobby (Phase 6) ---------------------------------------------
  mode: ServerMode | null;
  screen: Screen;
  /** Which tab of the between-raids menu is up — see `MenuTab` for why it is
   *  here and not in the component that draws the strip. */
  menuTab: MenuTab;
  /** What the market tab is narrowed to, or `null` for the whole market. Set by
   *  `openMarket` from a right-click anywhere in the menu. */
  marketFocus: MarketFocus | null;
  /** The fullscreen workbench and its plan, or `null` — see `WorkbenchState`. */
  workbench: WorkbenchState | null;

  // --- The walkable shelter: where the player is standing, in three slots. See
  // `HideoutSlice`.

  /** The module id whose station panel is open in the shelter, or `null`. One
   *  at a time: you can only stand at one bench. */
  hideoutStation: string | null;
  /** The gym's training minigame overlay is up. */
  hideoutGym: boolean;
  /** The range's readout while the player is at the lane, `null` otherwise. */
  hideoutRange: RangeHud | null;
  /** THE authoritative profile, as last sent. `null` on a direct server. */
  profile: ProfileWire | null;
  /**
   * `performance.now()` when the profile landed (0 with none). The hideout's
   * countdowns need to know how stale it is (`meta/hideoutLogic.ts`).
   */
  profileAtMs: number;
  /**
   * The ladder, or `null` while nobody asked or the ask missed. Fetched, not
   * pushed (`uiActions().fetchLeaderboard`).
   */
  leaderboard: LeaderboardRow[] | null;
  /** Where the queue stands, while `screen` is "queue". */
  queue: { queued: number; startsInMs: number | null } | null;
  /** The raid-end readout, while `screen` is "summary". */
  summary: RaidSummary | null;
  /**
   * `performance.now()` of the raid's hard end, computed once from `welcome`,
   * so the HUD timer runs off the frame. `null` on a direct server.
   */
  raidEndsAtMs: number | null;
  /**
   * The extraction being stood in. `secLeft` is the server's countdown; the HUD
   * never runs the clock.
   */
  extract: { name: string; secLeft: number } | null;
  /**
   * Display names of squadmates reported dead. Names, because the strip draws
   * `party.members` (account-level); `noteTeammateDeath` translates. An array
   * (reference compare), at most `PARTY_MAX`, append-only. A name collision
   * inside one party greys both — accepted.
   */
  squadDead: readonly string[];

  // --- Friends and the party ----------------------------------------------
  /**
   * Our own account handle from `/auth/session` — the one real identity
   * (`profile.name` is a display name). `null` on direct/offline. Survives
   * `resetHud` like `mode`: a fact about the browser, not the connection.
   */
  me: string | null;
  /** THE friends state, as last pushed. `null` before the first push. */
  friends: FriendsState | null;
  /** THE party, or `null` when we are in none. */
  party: PartyState | null;
  /**
   * The invite waiting to be answered, or `null`. Cleared on `partyRespond` and
   * when a `partyState` with a party arrives.
   */
  partyInvite: FriendRef | null;
  /** The last social refusal, for the friends tab to explain. */
  socialError: SocialError | null;

  // --- Inventory / looting --------------------------------------------------
  /**
   * True while the inventory screen is up. The GAME owns this (an input-mode
   * change); the UI asks via `uiActions().setInventoryOpen`.
   */
  invOpen: boolean;
  /**
   * True while the in-raid settings overlay is up. Game-owned like `invOpen`
   * (it releases the pointer); the UI asks via `uiActions().setSettingsOpen`.
   * Raid-scoped — between raids settings are a tab — and reset with the raid.
   */
  settingsOpen: boolean;
  /**
   * The inventory as believed: the last `inv` plus unconfirmed optimistic ops.
   * `null` until the first `inv`. Always a fresh object on change.
   */
  inv: Inventory | null;
  container: OpenContainer | null;
  /**
   * Search progress per target (a crate is one; a body up to three — pockets,
   * rig, backpack). Empty when nothing is open, always empty for the stash.
   * `ui/inventory/search.ts` owns the keys. Presentation only.
   */
  containerSearch: Readonly<Record<string, SearchView>>;
  /**
   * The weapon the Werkbank is open on, by iid, or `null`. An iid rather than
   * the instance because every op replaces the object; the pane re-resolves on
   * every render and closes itself when the gun stops resolving.
   */
  modWeaponIid: string | null;
  /**
   * The floating dialogs — opened boxes and inspected items — in STACKING order
   * (last = topmost; all overlays share one z-index, so DOM order stacks and
   * `raiseDialog` moves an entry to the end). Client-local: the client already
   * holds every answer, and there is no message for it. Iids, not instances,
   * re-resolved per render, so a subject that goes away closes its dialog. The
   * same iid may appear once per kind.
   */
  dialogs: DialogRef[];
  /** Interact hint from the world raycast, e.g. `"Open crate"`. `null` hides
   *  the prompt. The key label ("F") is the HUD's to render. */
  interactPrompt: string | null;
  /** `performance.now()` when the running med use completes, with its total
   *  duration for the progress bar. `null` when idle. */
  medCast: { endMs: number; durMs: number } | null;

  /**
   * A flashbang: when the white-out ends and how long it was, so a re-flash
   * restarts the curve. `null` when nothing is on screen.
   */
  flash: { endMs: number; durMs: number } | null;

  /** Limb hp, seven entries in `LIMB_IDS` order — same layout as the wire. */
  limbs: number[];
  /** Full hp per limb, same order. Constant, but kept here so `ui/` needs no
   *  import of the combat rules to draw a bar. */
  maxLimbs: number[];
  bleedMask: number;
  fracMask: number;
  /**
   * Hunger and thirst off `YouSnap.needs`. `null` before the first snapshot or
   * against an old server — the HUD then draws nothing.
   */
  needs: { energy: number; hydration: number } | null;
  mag: number;
  magSize: number;
  /**
   * Whether `mag` may be shown; false draws "?". `FireControl.magKnown` — the
   * server's `unknown` plus predicted shots.
   */
  magKnown: boolean;
  /** A magazine check is in the hands — the "Checking…" line, and why the
   *  number is about to come back. `YouSnap.weapon.checking`. */
  checking: boolean;
  /**
   * Rounds the next reload can reach AND the player can count. See
   * `InventoryController.reserveRounds`.
   */
  reserve: number;
  /** Some spare magazine holds an unknown number on top of `reserve`, so the
   *  HUD hangs a "+?" off it. `InventoryController.reserveUnknown`. */
  reserveUnknown: boolean;
  reloading: boolean;
  dead: boolean;
  /** What the death screen says; `null` while alive. */
  death: DeathInfo | null;
  hitMarker: HitMarker;
  connected: boolean;
}

const MAX_LIMBS: number[] = LIMB_IDS.map((id: LimbId) => LIMB_MAX_HP[id]);

/**
 * Everything that belongs to ONE raid. Leaving a raid and losing the connection
 * are different amounts of forgetting.
 */
type RaidSlice = Pick<
  HudState,
  | "invOpen"
  | "settingsOpen"
  | "inv"
  | "container"
  | "containerSearch"
  | "modWeaponIid"
  | "dialogs"
  | "interactPrompt"
  | "medCast"
  | "flash"
  | "limbs"
  | "maxLimbs"
  | "bleedMask"
  | "fracMask"
  | "needs"
  | "mag"
  | "magSize"
  | "magKnown"
  | "checking"
  | "reserve"
  | "reserveUnknown"
  | "reloading"
  | "dead"
  | "death"
  | "hitMarker"
  | "raidEndsAtMs"
  | "extract"
  | "squadDead"
>;

/** Full health, no ammo known yet: what the HUD shows before the first snapshot. */
function initialRaidState(): RaidSlice {
  return {
    invOpen: false,
    settingsOpen: false,
    inv: null,
    container: null,
    containerSearch: {},
    modWeaponIid: null,
    dialogs: [],
    interactPrompt: null,
    medCast: null,
    flash: null,
    limbs: MAX_LIMBS.slice(),
    maxLimbs: MAX_LIMBS,
    bleedMask: 0,
    fracMask: 0,
    // Nothing known yet, which is not the same as "full" — see `HudState.needs`.
    needs: null,
    mag: 0,
    magSize: 0,
    // "Known" is the resting state: nothing to be uncertain about before the
    // first snapshot.
    magKnown: true,
    checking: false,
    reserve: 0,
    reserveUnknown: false,
    reloading: false,
    dead: false,
    death: null,
    hitMarker: { at: 0, kind: "predicted" },
    raidEndsAtMs: null,
    extract: null,
    squadDead: [],
  };
}

/**
 * The menu's own state at its opening value. Both reset paths reach it, so the
 * menu reopens on Gear after every raid.
 */
type MenuSlice = Pick<HudState, "menuTab" | "marketFocus" | "workbench">;

function initialMenuState(): MenuSlice {
  return { menuTab: "gear", marketFocus: null, workbench: null };
}

/**
 * Where the player stands in the shelter: nowhere. Reset by both paths — a raid
 * or a dropped socket ends that absolutely.
 */
type HideoutSlice = Pick<HudState, "hideoutStation" | "hideoutGym" | "hideoutRange">;

function initialHideoutState(): HideoutSlice {
  return { hideoutStation: null, hideoutGym: false, hideoutRange: null };
}

function initialState(): HudState {
  return {
    ...initialRaidState(),
    ...initialMenuState(),
    ...initialHideoutState(),
    mode: null,
    screen: "connecting",
    profile: null,
    profileAtMs: 0,
    leaderboard: null,
    queue: null,
    summary: null,
    me: null,
    friends: null,
    party: null,
    partyInvite: null,
    socialError: null,
    connected: false,
  };
}

export const useHud = create<HudState>(() => initialState());

/** The health block of one snapshot. Assembled by the caller. */
export interface HudHealthPatch {
  limbs: number[];
  bleedMask: number;
  fracMask: number;
  dead: boolean;
  /** `null` when the server sends no needs block at all — see `HudState.needs`. */
  needs: { energy: number; hydration: number } | null;
}

/** One store write per snapshot — see rule 3 above. */
export function pushSnapshot(patch: HudHealthPatch): void {
  useHud.setState((prev) => ({
    limbs: patch.limbs,
    bleedMask: patch.bleedMask,
    fracMask: patch.fracMask,
    dead: patch.dead,
    // Same snapshot as the limbs, so the same `set()`.
    needs: patch.needs,
    // Cleared only on dead → alive: the `death` event (with the forensics)
    // often arrives a snapshot before the one reporting us dead.
    death: prev.dead && !patch.dead ? null : prev.death,
  }));
}

/** Predicted ammunition. Cheap to call every frame: no-ops when unchanged. */
export function setAmmo(
  mag: number,
  magSize: number,
  reserve: number,
  reloading: boolean,
  magKnown: boolean,
  checking: boolean,
  reserveUnknown: boolean,
): void {
  const s = useHud.getState();
  if (
    s.mag === mag &&
    s.magSize === magSize &&
    s.reserve === reserve &&
    s.reloading === reloading &&
    s.magKnown === magKnown &&
    s.checking === checking &&
    s.reserveUnknown === reserveUnknown
  ) {
    return;
  }
  useHud.setState({ mag, magSize, reserve, reloading, magKnown, checking, reserveUnknown });
}

export function setConnected(connected: boolean): void {
  if (useHud.getState().connected === connected) return;
  useHud.setState({ connected });
}

/**
 * The local player died — from the `death` event, the only message carrying
 * the forensics.
 */
export function setDeath(info: DeathInfo): void {
  useHud.setState({ dead: true, death: info });
}

export function pulseHitMarker(kind: HitMarkerKind, at: number): void {
  useHud.setState({ hitMarker: { at, kind } });
}

/**
 * A flashbang landed. The longer white-out wins by end time; `durMs` rides
 * along so the fade restarts from full.
 */
export function setFlash(sec: number, nowMs: number): void {
  const durMs = sec * 1000;
  const endMs = nowMs + durMs;
  const current = useHud.getState().flash;
  if (current !== null && current.endMs >= endMs) return;
  useHud.setState({ flash: { endMs, durMs } });
}

/**
 * Wipes back to the pre-connection state. `mode` and `me` survive (a meta
 * server may answer a reconnect with `welcome`; the party needs to know who we
 * are). Friends, party and invite go — the reconnect's `hello` refreshes them.
 */
export function resetHud(): void {
  forgetSquad();
  useHud.setState((s) => ({ ...initialState(), mode: s.mode, me: s.me }));
}

/**
 * Forgets THE RAID and nothing else. The shelter slice goes too (nobody comes
 * out of a raid standing at a workbench), and so does the menu slice, so the
 * menu reopens on Gear as it did while the tab was component state.
 */
export function resetRaidHud(): void {
  forgetSquad();
  useHud.setState({ ...initialRaidState(), ...initialMenuState(), ...initialHideoutState() });
}

// ---------------------------------------------------------------------------
// Inventory / looting setters. Same rules: plain data, one `set()`, no-op on no
// change.
// ---------------------------------------------------------------------------

/** Shows/hides the inventory screen. Called by the GAME (it owns input modes);
 *  the UI requests the change through `uiActions().setInventoryOpen`. */
export function setInvOpen(open: boolean): void {
  if (useHud.getState().invOpen === open) return;
  useHud.setState({ invOpen: open });
}

/** Shows/hides the in-raid settings overlay. Called by the GAME, which owns the
 *  cursor; the UI requests the change through `uiActions().setSettingsOpen`. */
export function setSettingsOpen(open: boolean): void {
  if (useHud.getState().settingsOpen === open) return;
  useHud.setState({ settingsOpen: open });
}

/** Replaces the believed inventory — server `inv`, or an optimistic op. */
export function setInventory(inv: Inventory | null): void {
  useHud.setState({ inv });
}

/** Replaces the open container (or clears it with `null`). */
export function setContainer(container: OpenContainer | null): void {
  useHud.setState({ container });
}

/** Replaces the whole per-target search map (`{}` clears it). Written only by
 *  `ui/inventory/search.ts`, which owns the machine and mints the keys. */
export function setContainerSearch(search: Readonly<Record<string, SearchView>>): void {
  useHud.setState({ containerSearch: search });
}

/**
 * Opens the Werkbank on a weapon, or closes it with `null`. A panel toggle
 * called from the UI — no input-mode change. Single, unlike `dialogs`: two
 * benches on one screen is a mess.
 */
export function setModWeapon(iid: string | null): void {
  if (useHud.getState().modWeaponIid === iid) return;
  useHud.setState({ modWeaponIid: iid });
}

// ---------------------------------------------------------------------------
// The dialog stack: four verbs over one array, all UI calls, nothing sent.
// ---------------------------------------------------------------------------

/**
 * Opens one dialog — or raises it when already open, since "bring it to the
 * front" is all a second open can mean.
 */
export function openDialog(t: DialogKind, iid: string): void {
  const dialogs = useHud.getState().dialogs;
  const rest = dialogs.filter((d) => !(d.t === t && d.iid === iid));
  useHud.setState({ dialogs: [...rest, { t, iid }] });
}

/** Takes one dialog down: its ×, or the screen noticing its subject is gone. */
export function closeDialog(t: DialogKind, iid: string): void {
  const dialogs = useHud.getState().dialogs;
  const rest = dialogs.filter((d) => !(d.t === t && d.iid === iid));
  if (rest.length !== dialogs.length) useHud.setState({ dialogs: rest });
}

/** Moves a dialog to the top. No-ops when already topmost or not open. */
export function raiseDialog(t: DialogKind, iid: string): void {
  const dialogs = useHud.getState().dialogs;
  const last = dialogs[dialogs.length - 1];
  if (last !== undefined && last.t === t && last.iid === iid) return;
  const rest = dialogs.filter((d) => !(d.t === t && d.iid === iid));
  if (rest.length === dialogs.length) return;
  useHud.setState({ dialogs: [...rest, { t, iid }] });
}

/** Closes every dialog: screen teardown. */
export function clearDialogs(): void {
  if (useHud.getState().dialogs.length === 0) return;
  useHud.setState({ dialogs: [] });
}

/**
 * Escape's answer: takes down the topmost window and says whether there was
 * one. The dialog stack first (last entry), then the Werkbank once the stack
 * is empty (screens draw the bench before the stack at the same z-index).
 */
export function closeTopDialog(): boolean {
  const { dialogs, modWeaponIid } = useHud.getState();
  if (dialogs.length > 0) {
    useHud.setState({ dialogs: dialogs.slice(0, -1) });
    return true;
  }
  if (modWeaponIid !== null) {
    useHud.setState({ modWeaponIid: null });
    return true;
  }
  return false;
}

/** Cheap to call every frame: no-ops while the text is unchanged. */
export function setInteractPrompt(text: string | null): void {
  if (useHud.getState().interactPrompt === text) return;
  useHud.setState({ interactPrompt: text });
}

/** Starts (object) or clears (`null`) the med-use progress bar. */
export function setMedCast(medCast: { endMs: number; durMs: number } | null): void {
  useHud.setState({ medCast });
}

// ---------------------------------------------------------------------------
// Meta / lobby setters. Same rules again.
// ---------------------------------------------------------------------------

/** Records which kind of server this is. Written once per session. */
export function setMode(mode: ServerMode): void {
  if (useHud.getState().mode === mode) return;
  useHud.setState({ mode });
}

export function setScreen(screen: Screen): void {
  if (useHud.getState().screen === screen) return;
  useHud.setState({ screen });
}

/** Moves the menu to another tab. The strip's own click, and no-ops on the tab
 *  already showing. */
export function setMenuTab(tab: MenuTab): void {
  if (useHud.getState().menuTab === tab) return;
  useHud.setState({ menuTab: tab });
}

/**
 * Shows the market narrowed to one thing. One `setState` for tab and focus,
 * or the market would render once with the old focus. `seq` counts requests.
 */
export function openMarket(t: "item" | "linked", defId: string): void {
  const prev = useHud.getState().marketFocus;
  useHud.setState({ menuTab: "market", marketFocus: { t, defId, seq: (prev?.seq ?? 0) + 1 } });
}

/**
 * Shows the market narrowed to the parts one workbench plan is short of — the
 * "Buy N missing parts" button. Same single `setState` and same `seq` as
 * `openMarket`; the bench itself stays open behind the tab.
 */
export function openMarketForBuild(defIds: readonly string[]): void {
  const prev = useHud.getState().marketFocus;
  useHud.setState({
    menuTab: "market",
    marketFocus: { t: "build", defIds, seq: (prev?.seq ?? 0) + 1 },
  });
}

/**
 * Widens the market back out — the chip's × only. Leaving the tab keeps the
 * focus.
 */
export function clearMarketFocus(): void {
  if (useHud.getState().marketFocus === null) return;
  useHud.setState({ marketFocus: null });
}

// ---------------------------------------------------------------------------
// The fullscreen workbench. UI-local like the dialogs: nothing is sent until
// the player presses Assemble, and that goes out as ordinary inventory ops.
// ---------------------------------------------------------------------------

/**
 * Puts a gun on the bench. The PLAN survives reopening the SAME gun (the
 * player who tabbed away to buy a scope comes back to their build); another
 * gun starts blank. The floating Werkbank closes — one bench at a time.
 */
export function openWorkbench(iid: string): void {
  const prev = useHud.getState().workbench;
  useHud.setState({
    workbench: { weaponIid: iid, plan: prev?.weaponIid === iid ? prev.plan : {}, open: true },
    modWeaponIid: null,
  });
}

/** Shows or hides the bench without forgetting the plan. No-op with no bench. */
export function setWorkbenchOpen(open: boolean): void {
  const prev = useHud.getState().workbench;
  if (prev === null || prev.open === open) return;
  useHud.setState({ workbench: { ...prev, open } });
}

/** Replaces the plan — one pick, or `{}` for the gun as it stands. */
export function setWorkbenchPlan(plan: BuildPlan): void {
  const prev = useHud.getState().workbench;
  if (prev === null) return;
  useHud.setState({ workbench: { ...prev, plan } });
}

/** Forgets the bench and its plan: the gun is gone, or the player is done. */
export function closeWorkbench(): void {
  if (useHud.getState().workbench === null) return;
  useHud.setState({ workbench: null });
}

// ---------------------------------------------------------------------------
// The walkable shelter. Written by the GAME, read by the overlay. Same rules.
// ---------------------------------------------------------------------------

/** Opens ONE station's panel in the shelter (by module id), or closes whatever
 *  is open with `null`. No-ops when the same thing is already up. */
export function setHideoutStation(id: string | null): void {
  if (useHud.getState().hideoutStation === id) return;
  useHud.setState({ hideoutStation: id });
}

/** Shows/hides the gym's training overlay. The GAME's call, like `invOpen`:
 *  the overlay is up because the pointer was handed to the DOM for it. */
export function setHideoutGym(open: boolean): void {
  if (useHud.getState().hideoutGym === open) return;
  useHud.setState({ hideoutGym: open });
}

/**
 * The range's readout, or `null` away from the lane. Called every frame, so it
 * compares field by field like `setAmmo`.
 */
export function setHideoutRange(hud: RangeHud | null): void {
  const prev = useHud.getState().hideoutRange;
  if (prev === null && hud === null) return;
  if (
    prev !== null &&
    hud !== null &&
    prev.weaponName === hud.weaponName &&
    prev.shots === hud.shots &&
    prev.hits === hud.hits &&
    prev.drillSecLeft === hud.drillSecLeft &&
    prev.drillScore === hud.drillScore &&
    prev.best === hud.best
  ) {
    return;
  }
  useHud.setState({ hideoutRange: hud });
}

/**
 * Replaces the believed profile, whole. The arrival is stamped on the
 * HIDEOUT's identity, not the profile's: optimistic drags build new profiles
 * around the same hideout, and re-stamping would rewind every countdown.
 */
export function setProfile(profile: ProfileWire | null): void {
  const prev = useHud.getState().profile;
  const sameHideout = prev !== null && profile !== null && prev.hideout === profile.hideout;
  useHud.setState(
    profile === null || sameHideout ? { profile } : { profile, profileAtMs: performance.now() },
  );
}

/**
 * Replaces the fetched ladder, or forgets it with `null`. Whole, never a delta.
 */
export function setLeaderboard(leaderboard: LeaderboardRow[] | null): void {
  useHud.setState({ leaderboard });
}

export function setQueue(queue: HudState["queue"]): void {
  useHud.setState({ queue });
}

export function setSummary(summary: RaidSummary | null): void {
  useHud.setState({ summary });
}

/** The raid's hard deadline, in `performance.now()` terms. Set once per raid. */
export function setRaidEndsAt(atMs: number | null): void {
  if (useHud.getState().raidEndsAtMs === atMs) return;
  useHud.setState({ raidEndsAtMs: atMs });
}

/**
 * The extraction being stood in. Called every snapshot; no-ops on the identical
 * readout.
 */
export function setExtract(extract: HudState["extract"]): void {
  const prev = useHud.getState().extract;
  if (prev === null && extract === null) return;
  if (
    prev !== null &&
    extract !== null &&
    prev.name === extract.name &&
    prev.secLeft === extract.secLeft
  ) {
    return;
  }
  useHud.setState({ extract });
}

// ---------------------------------------------------------------------------
// Squad awareness in raid (protocol v24)
// ---------------------------------------------------------------------------

/**
 * Entity id → display name for every entity flagged `team`. Module state, not
 * store state: written from the snapshot handler and rendered by nothing —
 * only `squadDead` is derived from it. Grows for the raid (a dead squadmate
 * leaves the snapshots); both reset paths empty it.
 */
const squadEntityNames = new Map<string, string>();

/**
 * Remembers one entity the server says is ours. Called per flagged entity per
 * snapshot — a `Map.set` and nothing else.
 */
export function noteTeammate(id: string, name: string): void {
  squadEntityNames.set(id, name);
}

/**
 * A `death` event. No-op unless the id is a known squadmate; the re-append
 * guard keeps a bug from growing a duplicate line.
 */
export function noteTeammateDeath(id: string): void {
  const name = squadEntityNames.get(id);
  if (name === undefined) return;
  const prev = useHud.getState().squadDead;
  if (prev.includes(name)) return;
  useHud.setState({ squadDead: [...prev, name] });
}

/** Forgets who was on our side. Both reset paths call it — the registry is not
 *  part of the store's state and so is not covered by `setState`. */
function forgetSquad(): void {
  squadEntityNames.clear();
}

// ---------------------------------------------------------------------------
// Friends and the party. Whole-state pushes — plain replacements.
// ---------------------------------------------------------------------------

/** Our own account handle. Written once at boot, from the session. */
export function setMe(me: string | null): void {
  if (useHud.getState().me === me) return;
  useHud.setState({ me });
}

/** Replaces the believed friends state — always whole, never a delta. */
export function setFriends(friends: FriendsState | null): void {
  useHud.setState({ friends });
}

/** Replaces the believed party (`null` = we are in none). */
export function setParty(party: PartyState | null): void {
  useHud.setState({ party });
}

/** Shows the invite prompt, or takes it down with `null`. */
export function setPartyInvite(from: FriendRef | null): void {
  useHud.setState({ partyInvite: from });
}

/** The last social refusal, or `null` to forget it. */
export function setSocialError(socialError: SocialError | null): void {
  if (useHud.getState().socialError === null && socialError === null) return;
  useHud.setState({ socialError });
}
